/**
 * L'identité d'envoi d'une conversation : un établissement ET la fiche client de
 * cet établissement — retour Odomo 2026-09 (un message d'arrivée d'un hôtel parti
 * vers la cliente d'un autre, depuis la vue groupe).
 *
 * Calculée par le serveur (`SendingFacilityResolver`, champ `sending_facilities`
 * de `/customer-basics/{id}`). Module FEUILLE : partagé par le web et le mobile,
 * il n'importe ni `pages/`, ni `components/`, ni i18next.
 */
export interface SendingFacility {
    facility_id: number;
    facility_name: string;
    /** La fiche client DE cet établissement : c'est elle que vise l'envoi. */
    customer_id: number;
    is_group: boolean;
    has_whatsapp: boolean;
    /** Fenêtre de 24 h de CETTE fiche sur CET établissement ; `null` = fermée. */
    whatsapp_free_text_until: string | null;
}

export interface SendingFacilityCustomer {
    id: number;
    facility_id?: number | null;
    facility_name?: string | null;
    whatsapp_free_text_until?: string | null;
    sending_facilities?: SendingFacility[] | null;
}

/**
 * Les identités proposées. Sans le champ serveur (réponse antérieure au
 * déploiement), la fiche elle-même — jamais l'établissement du sélecteur, qui est
 * le groupe en vue groupe. WhatsApp n'y est pas garanti : le serveur tranchera.
 */
export function sendingFacilityOptions(customer?: SendingFacilityCustomer): SendingFacility[] {
    if (!customer) return [];
    if (customer.sending_facilities) return customer.sending_facilities;
    if (!customer.facility_id) return [];

    return [{
        facility_id: customer.facility_id,
        facility_name: customer.facility_name ?? '',
        customer_id: customer.id,
        is_group: false,
        has_whatsapp: false,
        whatsapp_free_text_until: customer.whatsapp_free_text_until ?? null,
    }];
}

/** Une identité seule s'impose ; parmi plusieurs, seul un choix explicite compte. */
export function resolveSendingFacility(
    options: SendingFacility[],
    chosenFacilityId: number | null,
): SendingFacility | null {
    if (options.length === 1) return options[0];

    return options.find((option) => option.facility_id === chosenFacilityId) ?? null;
}

/**
 * Afficher « Envoyé en tant que » : en vue groupe, quand il y a un choix, ou quand
 * l'hôtel d'envoi n'est pas l'établissement sélectionné. Jamais en vue hôtel
 * ordinaire, où ce serait du bruit.
 */
export function shouldShowSendingFacility(
    options: SendingFacility[],
    currentFacilityId: number | undefined,
    isCurrentFacilityGroup: boolean,
): boolean {
    if (options.length === 0) return false;
    if (options.length > 1 || isCurrentFacilityGroup) return true;

    return options[0].facility_id !== currentFacilityId;
}
